import { Activity, BookOpen, Info, Map } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useLocale } from "../i18n/LocaleContext";
import { LOCALES, localizePathname, writeLocalePreference } from "../i18n/locale";
import { TRUST_LINKS } from "../trust/content";
import "../trust/trust.css";
import { HowToRead } from "./HowToRead";
import packageMetadata from "../../package.json";

export function isToolPathname(pathname: string) {
  return pathname === "/" || pathname === "/map" || pathname.startsWith("/forecast/");
}

export function isDocsPathname(pathname: string) {
  return pathname === "/about" || pathname === "/methodology" || pathname === "/privacy";
}

function primarySectionForPathname(pathname: string) {
  if (pathname === "/map") {
    return "map";
  }

  if (isDocsPathname(pathname)) {
    return "about";
  }

  return "windgram";
}

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { barePathname, locale, pathname } = useLocale();
  const panelId = useId();
  const closeFocusRef = useRef(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [isHowToReadOpen, setIsHowToReadOpen] = useState(false);
  const currentPrimarySection = primarySectionForPathname(
    barePathname === "/" ? location.pathname : barePathname
  );

  useEffect(() => {
    setIsHowToReadOpen(false);
  }, [barePathname]);

  useEffect(() => {
    if (!isHowToReadOpen) {
      if (closeFocusRef.current) {
        triggerRef.current?.focus();
        closeFocusRef.current = false;
      }
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        closeFocusRef.current = true;
        setIsHowToReadOpen(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isHowToReadOpen]);

  function openHowToRead() {
    setIsHowToReadOpen(true);
  }

  function closeHowToRead() {
    closeFocusRef.current = true;
    setIsHowToReadOpen(false);
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <NavLink className="brand" to={pathname("/")} aria-label={t("a11y.brand")}>
          <strong>Lifty</strong>
        </NavLink>

        <nav className="primary-nav" aria-label={t("nav.primary")}>
          <Link
            aria-current={currentPrimarySection === "windgram" ? "page" : undefined}
            className={currentPrimarySection === "windgram" ? "active" : undefined}
            to={pathname("/")}
          >
            <Activity size={17} />
            {t("nav.windgram")}
          </Link>
          <Link
            aria-current={currentPrimarySection === "map" ? "page" : undefined}
            className={currentPrimarySection === "map" ? "active" : undefined}
            to={pathname("/map")}
          >
            <Map size={17} />
            {t("nav.thermalMap")}
          </Link>
          <Link
            aria-current={currentPrimarySection === "about" ? "page" : undefined}
            className={currentPrimarySection === "about" ? "active" : undefined}
            to={pathname("/about")}
          >
            <Info size={17} />
            {t("nav.about")}
          </Link>
        </nav>

        <div className="topbar-meta">
          <label className="locale-control">
            <span className="sr-only">{t("language.label")}</span>
            <select
              aria-label={t("language.label")}
              value={locale}
              onChange={(event) => {
                const nextLocale = event.target.value as typeof locale;
                writeLocalePreference(nextLocale);
                navigate(`${localizePathname(barePathname, nextLocale)}${location.search}${location.hash}`);
              }}
            >
              {LOCALES.map((entry) => (
                <option key={entry.tag} value={entry.tag}>
                  {t(`language.${entry.tag.replaceAll("-", "")}`)}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      <div className="shell-reference-bar">
        <nav className="shell-docs-nav" aria-label={t("nav.references")}>
          <NavLink to={pathname("/methodology")}>{t("nav.methodology")}</NavLink>
          <NavLink to={pathname("/privacy")}>{t("nav.privacy")}</NavLink>
          <NavLink to={pathname("/about")}>
            {t("nav.credits")}
          </NavLink>
          <a href={TRUST_LINKS.githubIssues} rel="noreferrer" target="_blank">
            {t("nav.feedback")}
          </a>
        </nav>

        <div className="shell-guide-bar">
          <button
            aria-controls={panelId}
            aria-expanded={isHowToReadOpen}
            className="shell-trust-trigger"
            onClick={isHowToReadOpen ? closeHowToRead : openHowToRead}
            ref={triggerRef}
            type="button"
          >
            <BookOpen aria-hidden="true" size={14} />
            {t("nav.guide")}
          </button>
        </div>
      </div>

      <div className="shell-trust-drawer" hidden={!isHowToReadOpen}>
        <HowToRead
          className="shell-how-to-read-panel"
          headingAs="h2"
          onClose={closeHowToRead}
          panelId={panelId}
          titleId={`${panelId}-title`}
        />
      </div>

      <main>
        <Outlet />
      </main>

      <footer className="footer shell-footer">
        <div className="footer-brand">
          <NavLink to={pathname("/")} aria-label={t("a11y.brand")}>
            Lifty
          </NavLink>
          <span>{t("nav.tagline")}</span>
        </div>

        <nav className="footer-nav shell-footer-primary" aria-label={t("nav.footer")}>
          <NavLink to={pathname("/")} end>
            {t("nav.windgram")}
          </NavLink>
          <NavLink to={pathname("/map")}>{t("nav.thermalMap")}</NavLink>
          <NavLink to={pathname("/about")}>{t("nav.credits")}</NavLink>
        </nav>

        <nav
          className="shell-footer-secondary"
          aria-label={t("nav.references")}
        >
          <NavLink to={pathname("/methodology")}>{t("nav.methodology")}</NavLink>
          <NavLink to={pathname("/privacy")}>{t("nav.privacy")}</NavLink>
          <a href={TRUST_LINKS.githubIssues} rel="noreferrer" target="_blank">
            {t("nav.feedback")}
          </a>
        </nav>

        <div className="footer-meta">
          <div>
            <span>{t("models.autoDescription")}</span>
            <span className="footer-version">v{packageMetadata.version}</span>
          </div>
          <small>
            {t("trust.caution")}
          </small>
        </div>
      </footer>
    </div>
  );
}
